// Power-validated CdA: back-solve CdA from BLE power + speed.
//
// When the rider has a power meter, we can derive their CdA directly from
// the physics relation:
//
//   P_meter = (P_drag + P_rolling + P_gravity) / drivetrain_efficiency
//
// Rearranging for CdA, with P_drag = 0.5·ρ·CdA·v³:
//
//   CdA = (P_meter · η - Crr·m·g·v - m·g·grade·v) · 2 / (ρ · v³)
//
// This is the closed form — no iteration needed. We keep iteration around
// for the *speed-from-power-and-CdA* direction (the existing physics.py
// predict_speed already does that). Here we go the other way and produce
// a CdA the camera can be cross-checked against.
//
// Constants match platform/shared/aero/physics.py defaults so the numbers
// line up with the server-side calculator.

const RHO_AIR = 1.225;             // kg/m³ at sea level, 15 °C
const CRR = 0.005;                 // road tyres on a trainer
const M = 75.0;                    // rider + bike, kg (default until profile lands)
const G = 9.81;                    // m/s²
const ETA = 0.97;                  // drivetrain efficiency
const GRADE = 0.0;                 // indoor trainer is flat

const MIN_VELOCITY = 2.0;          // m/s (~7.2 km/h). Below this, CdA solve is
                                   // unreliable because rolling/gravity dominate.
const MIN_POWER = 30;              // W. Below this we're not really pedalling.

export interface PowerCdaInputs {
  /** Instantaneous power from the BLE power meter, watts. */
  powerW: number;
  /** Velocity in m/s. Comes from BLE speed when available, or from the vision
   *  pipeline's predicted speed otherwise (see comment in computePowerCda). */
  velocityMps: number;
  /** Optional overrides — when a rider profile is loaded (Track 1.5) these
   *  will come from the rider's record, otherwise defaults are used. */
  riderMassKg?: number;
  rollingResistance?: number;
  drivetrainEfficiency?: number;
  airDensity?: number;
  grade?: number;
}

export interface PowerCdaResult {
  /** Back-solved CdA in m². null when inputs aren't usable. */
  cda: number | null;
  /** Velocity used for the solve. Echoed so the caller can decide if they
   *  trust it (it might be a vision prediction rather than a measured speed). */
  velocityMps: number;
  /** Reason the solve was skipped, for debug. */
  reason?: 'no_power' | 'no_velocity' | 'below_min_velocity' | 'below_min_power' | 'negative_drag';
}

/**
 * Back-solve CdA from instantaneous power + velocity.
 *
 * Returns null in `cda` when inputs are out of bounds (no power, too slow,
 * etc.). The caller — typically useVisionPipeline — decides what to do with
 * a null (usually: don't show the cross-check pill).
 */
export function computePowerCda(inputs: PowerCdaInputs): PowerCdaResult {
  const {
    powerW,
    velocityMps,
    riderMassKg = M,
    rollingResistance = CRR,
    drivetrainEfficiency = ETA,
    airDensity = RHO_AIR,
    grade = GRADE,
  } = inputs;

  if (!Number.isFinite(powerW) || powerW <= 0) {
    return { cda: null, velocityMps, reason: 'no_power' };
  }
  if (powerW < MIN_POWER) {
    return { cda: null, velocityMps, reason: 'below_min_power' };
  }
  if (!Number.isFinite(velocityMps) || velocityMps <= 0) {
    return { cda: null, velocityMps, reason: 'no_velocity' };
  }
  if (velocityMps < MIN_VELOCITY) {
    return { cda: null, velocityMps, reason: 'below_min_velocity' };
  }

  // Power available for drag, after subtracting rolling + gravity losses and
  // accounting for drivetrain efficiency.
  const pAtWheel = powerW * drivetrainEfficiency;
  const pRolling = rollingResistance * riderMassKg * G * velocityMps;
  const pGravity = riderMassKg * G * grade * velocityMps;
  const pDrag = pAtWheel - pRolling - pGravity;

  if (pDrag <= 0) {
    // Rolling/gravity already exceed the power — usually means we're coasting
    // or descending. Don't pretend we can solve for CdA here.
    return { cda: null, velocityMps, reason: 'negative_drag' };
  }

  // P_drag = 0.5·ρ·CdA·v³  →  CdA = 2·P_drag / (ρ·v³)
  const cda = (2 * pDrag) / (airDensity * velocityMps ** 3);

  return { cda, velocityMps };
}

/**
 * Agreement score between vision-CdA and power-CdA, in [0, 1].
 *
 * Tuned so:
 *   - within 5% → 1.0 (riders see "the camera and the power meter agree")
 *   - 5–15%      → linear ramp to 0.5
 *   - >15%       → 0 (flags a calibration confidence warning in the UI)
 *
 * Returns null when either input is null.
 */
export function powerVisionAgreement(
  visionCda: number | null,
  powerCda: number | null,
): number | null {
  if (visionCda == null || powerCda == null) return null;
  if (visionCda <= 0 || powerCda <= 0) return null;

  const relErr = Math.abs(visionCda - powerCda) / ((visionCda + powerCda) / 2);

  if (relErr <= 0.05) return 1.0;
  if (relErr >= 0.15) return 0.0;
  // Linear ramp from 1.0 at 5% to 0 at 15%
  return 1.0 - (relErr - 0.05) / 0.10;
}
